// frontend/shared/api/workplaces.ts

import { httpClient } from './http'
// Enterprise 类型保留用于未来扩展（如 getEnterpriseWorkplaceTree 返回类型增强）
// import type { Enterprise } from './enterprises'

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

function toRecord(value: unknown): Record<string, unknown> {
  return isRecord(value) ? value : {}
}

/**
 * 职场基本信息
 */
export interface Workplace {
  id: string
  name: string
  enterprise_id: string
  province: string
  city: string
  district: string
  address: string
  estimated_employees?: number
  contact_person?: string
  contact_phone?: string
  latitude?: number
  longitude?: number
  created_at: string
  updated_at: string
}

/**
 * 企业-职场树节点（用于排期管理等场景）
 */
export interface EnterpriseWorkplaceTreeNode {
  id: string
  label: string
  type: 'enterprise' | 'workplace'
  enterprise_id?: string
  workplace_id?: string
  children?: EnterpriseWorkplaceTreeNode[]
}

/**
 * 职场列表项（用于左侧导航）
 */
export interface WorkplaceListItem {
  id: string
  name: string
  hasIndependentConfig: boolean  // 是否有独立日历配置
}

/**
 * 获取职场列表（企业管理员）
 * @returns 职场列表
 *
 * 说明：
 * - 对应契约：GET /api/v1/workplaces/
 * - 企业管理员：后端按当前登录用户 enterprise 自动过滤
 * - ⚠️ 运营端不要调用该函数（会 403：需要企业管理员并关联企业）
 * @returns 职场列表
 */
export async function listWorkplacesForEnterpriseAdmin(): Promise<Workplace[]> {
  const response = await httpClient.get<Workplace[]>(
    'workplaces/'
  )
  return Array.isArray(response) ? response : []
}

/**
 * 获取企业下的职场列表（运营端 operator-admin）
 * @param enterpriseId 企业ID（契约路径参数 enterprises/{enterprise_pk}/workplaces/）
 * @returns 职场列表
 *
 * 说明：
 * - 对应契约：GET /api/v1/enterprises/{enterprise_pk}/workplaces/
 * - ⚠️ 该接口与 /api/v1/workplaces/ 不是同一权限模型
 */
export async function listWorkplacesForOperatorAdmin(
  enterpriseId: string | number
): Promise<Workplace[]> {
  if (enterpriseId === undefined || enterpriseId === null || enterpriseId === '') {
    console.warn('[Workplaces API] operator-admin 获取企业职场失败：enterpriseId 为空')
    return []
  }

  const resp = await httpClient.get<unknown>(
    `enterprises/${enterpriseId}/workplaces/`,
    { params: { page_size: 1000 } }
  )
  if (Array.isArray(resp)) return resp as Workplace[]
  if (isRecord(resp) && Array.isArray(resp.results)) return resp.results as Workplace[]
  return []
}

/**
 * 批量查询职场日历配置状态
 * @param enterpriseId 企业ID（此参数保留用于类型兼容，但实际不使用）
 * @param year 年份
 * @returns 职场配置状态列表
 */
export async function getWorkplacesCalendarStatus(
  enterpriseId: string | number,
  year: number,
  options?: { forOperatorAdmin?: boolean }
): Promise<Array<{ workplace_id: string; has_config: boolean }>> {
  try {
    const forOperatorAdmin = !!options?.forOperatorAdmin

    if (!forOperatorAdmin) {
      // ✅ 企业管理员：GET /api/v1/workplaces/calendar-status/?year=...
      const response = await httpClient.get<Array<{ workplace_id: string; has_config: boolean }>>(
        'workplaces/calendar-status/',
        { params: { year } }
      )
      return Array.isArray(response) ? response : []
    }

    // ✅ 运营端：GET /api/v1/enterprises/{id}/workplaces-calendar/status/?year=...
    if (enterpriseId === undefined || enterpriseId === null || enterpriseId === '') {
      console.warn('[Workplaces API] operator-admin 获取职场日历状态失败：enterpriseId 为空')
      return []
    }

    const response = await httpClient.get<Array<{ workplace_id: string; has_config: boolean }>>(
      `enterprises/${enterpriseId}/workplaces-calendar/status/`,
      { params: { year } }
    )
    return Array.isArray(response) ? response : []
  } catch (error) {
    console.warn('[Workplaces API] 获取职场日历状态失败，返回空数组', error)
    return []
  }
}

/**
 * 兼容旧函数名（不建议继续使用）
 * ⚠️ 该函数曾导致 operator-admin 误调用企业管理员端点，后续请逐步替换为：
 * - listWorkplacesForEnterpriseAdmin()
 * - listWorkplacesForOperatorAdmin(enterpriseId)
 */
export async function getEnterpriseWorkplaces(
  enterpriseId: string | number,
  options?: { forOperatorAdmin?: boolean }
): Promise<Workplace[]> {
  if (options?.forOperatorAdmin) {
    return await listWorkplacesForOperatorAdmin(enterpriseId)
  }
  return await listWorkplacesForEnterpriseAdmin()
}

/**
 * 获取企业-职场树形数据（用于排期管理等场景）
 * @returns 企业-职场树节点数组
 * 
 * 数据结构：
 * - 企业节点：{ id: 'enterprise-{id}', label: 企业名称, type: 'enterprise', enterprise_id: 'xxx', children: [职场节点] }
 * - 职场节点：{ id: 'workplace-{id}', label: 职场名称, type: 'workplace', enterprise_id: 'xxx', workplace_id: 'xxx' }
 * 
 * ⚠️ 修复 (2025-11-17)：
 * 1. 处理分页，获取所有企业（不只是第一页的20条）
 * 2. 使用正确的字段名 `name` 而不是 `enterprise_name`
 */
export async function getEnterpriseWorkplaceTree(): Promise<EnterpriseWorkplaceTreeNode[]> {
  try {
    console.debug('[Workplaces API] 开始获取企业-职场树形数据')
    
    // 1. 获取所有企业（处理分页）
    const allEnterprises: Array<{ id: string | number; name?: string }> = []
    let currentPage = 1
    let hasMore = true
    const pageSize = 100
    
    while (hasMore) {
      try {
        const enterprisesResponse = await httpClient.getAxiosInstance().get<{results: unknown[], count: number, next: string|null}>('enterprises/', {
          params: {
            page: currentPage,
            page_size: pageSize
          }
        })
        
        const pageData = enterprisesResponse.data
        const enterprises = Array.isArray(pageData?.results) ? pageData.results : []
        
        const normalizedEnterprises: Array<{ id: string | number; name?: string }> = []
        for (const it of enterprises) {
          const r = toRecord(it)
          const id = r.id
          if (id === undefined || id === null || id === '') continue
          normalizedEnterprises.push({
            id: String(id),
            name: typeof r.name === 'string' ? r.name : undefined
          })
        }

        if (normalizedEnterprises.length > 0) {
          allEnterprises.push(...normalizedEnterprises)
          console.debug(`[Workplaces API] 第 ${currentPage} 页获取到 ${normalizedEnterprises.length} 个企业`)
        }
        
        // 检查是否还有更多数据（只要有next就继续）
        hasMore = !!pageData?.next
        currentPage++
        
        // 如果当前页没有数据，也停止
        if (enterprises.length === 0) {
          hasMore = false
        }
        
        // 安全限制：最多获取10页（1000个企业）
        if (currentPage > 10) {
          console.warn('[Workplaces API] 已达到最大页数限制（10页）')
          break
        }
      } catch (error) {
        console.error(`[Workplaces API] 获取第 ${currentPage} 页企业失败:`, error)
        break
      }
    }
    
    console.debug('[Workplaces API] 总共获取到企业数量:', allEnterprises.length)
    
    // 2. 对每个企业获取其职场列表
    const treeNodes: EnterpriseWorkplaceTreeNode[] = []
    
    for (const enterprise of allEnterprises) {
      try {
        // ⚠️ 关键修复：使用 name 字段而不是 enterprise_name
        const enterpriseName = enterprise.name || `企业${enterprise.id}`
        
        // 获取该企业的职场列表
        const workplacesResponse = await httpClient.getAxiosInstance().get<{results: Workplace[]}>(`enterprises/${enterprise.id}/workplaces/`, {
          params: {
            page_size: 1000  // 获取该企业所有职场
          }
        })
        
        const workplaces = workplacesResponse.data?.results || []
        console.debug(`[Workplaces API] 企业 ${enterpriseName} (ID: ${enterprise.id}) 的职场数量:`, workplaces.length)
        
        // 构建职场节点
        const workplaceNodes: EnterpriseWorkplaceTreeNode[] = workplaces.map(workplace => ({
          id: `workplace-${workplace.id}`,
          label: workplace.name || `职场${workplace.id}`,
          type: 'workplace' as const,
          enterprise_id: String(enterprise.id),
          workplace_id: String(workplace.id)
        }))
        
        // 构建企业节点（只添加有职场的企业）
        if (workplaceNodes.length > 0) {
          treeNodes.push({
            id: `enterprise-${enterprise.id}`,
            label: enterpriseName,
            type: 'enterprise' as const,
            enterprise_id: String(enterprise.id),
            children: workplaceNodes
          })
        } else {
          console.debug(`[Workplaces API] 企业 ${enterpriseName} 没有职场，跳过`)
        }
      } catch (error) {
        console.warn(`[Workplaces API] 获取企业 ${enterprise.id} 的职场列表失败:`, error)
        // 继续处理下一个企业
      }
    }
    
    console.debug('[Workplaces API] 最终树节点数量:', treeNodes.length)
    return treeNodes
    
  } catch (error) {
    console.error('[Workplaces API] 获取企业-职场树失败:', error)
    return []
  }
}

/**
 * 职场配置接口（简化版）
 */
export interface WorkplaceConfig {
  workplace_id: string
  workplace_name: string
  workdays: number[]  // 工作日列表（0-6，0=周日）- 向后兼容（等同 week_workdays）
  meal_segments: string[]  // 可用餐段列表（code）
  special_demands: string[]  // 特殊需求
  smart_cabinet_enabled: boolean  // 是否启用智能餐柜
  smart_supplement_enabled: boolean  // 是否启用智能补单
  // M14-P2 新增：完整日历配置
  work_mode?: 'normal' | 'big_small_week_1' | 'big_small_week_2' | 'custom' | 'full_week'  // 工作模式
  week_workdays?: number[]  // 周工作日规则 [1,2,3,4,5]
  use_legal_holidays?: boolean  // 是否叠加法定节假日
  cycle_start_date?: string | null  // 大小周起始日期 (YYYY-MM-DD)
  holidays?: string[]  // 临时假期覆写 (YYYY-MM-DD[])
  workdays_override?: string[]  // 调休工作日覆写 (YYYY-MM-DD[])
}

/**
 * 时间规则详情
 * 
 * 字段说明:
 * - order_cutoff_time: 订餐截止时间 (HH:MM)
 * - cutoff_day_offset: 截止偏移天数（0=当天，-1=前一天）
 * - delivery_start_time: 配送开始时间 (HH:MM)
 * - delivery_end_time: 配送结束时间 (HH:MM)
 * - pickup_start_time: 取餐开始时间 (HH:MM)
 * - pickup_end_time: 取餐结束时间 (HH:MM)
 * - cabinet_clear_time: 餐柜清柜时间 (HH:MM)
 * - reservation_max_days: 最大预订天数
 */
export interface MealTimeRuleDetail {
  id: number
  name: string
  order_cutoff_time: string | null  // HH:MM
  cutoff_day_offset: number         // ⭐ 截止偏移天数（0=当天，-1=前一天）
  grace_period_minutes: number
  delivery_start_time: string | null  // HH:MM
  delivery_end_time: string | null    // HH:MM
  pickup_start_time: string | null    // HH:MM
  pickup_end_time: string | null      // HH:MM
  // ⭐ M11-NEW: 新增字段
  cabinet_clear_time: string | null   // HH:MM - 餐柜清柜时间
  reservation_max_days: number | null // 最大预订天数
  order_start_time: string | null     // HH:MM - 订餐开始时间
}

/**
 * 餐段详细配置
 */
export interface MealSegmentConfigDetail {
  code: string
  label: string
  meal_price: number
  meal_price_visible: boolean
  smart_auto_order_enabled: boolean
  grace_minutes: number
  time_rule_id: number | null
  time_rule: MealTimeRuleDetail | null
}

/**
 * 职场完整配置接口（含餐段时间规则）
 */
export interface WorkplaceFullConfig {
  workplace_id: string
  workplace_name: string
  workplace_address: string
  workdays: number[]  // 向后兼容（等同 week_workdays）
  meal_segments: string[]
  meal_segments_detail: MealSegmentConfigDetail[]  // ⭐ 餐段详情含时间规则
  consumption_scenes: string[]  // 消费场景列表
  special_demands: string[]
  smart_cabinet_enabled: boolean
  smart_supplement_enabled: boolean
  // M14-P2 新增：完整日历配置
  work_mode?: 'normal' | 'big_small_week_1' | 'big_small_week_2' | 'custom' | 'full_week'  // 工作模式
  week_workdays?: number[]  // 周工作日规则 [1,2,3,4,5]
  use_legal_holidays?: boolean  // 是否叠加法定节假日
  cycle_start_date?: string | null  // 大小周起始日期 (YYYY-MM-DD)
  holidays?: string[]  // 临时假期覆写 (YYYY-MM-DD[])
  workdays_override?: string[]  // 调休工作日覆写 (YYYY-MM-DD[])
}

/**
 * 获取职场配置（用于商户端创建排期）- 简化版
 * @param workplaceId 职场ID
 * @returns 职场配置
 */
export async function getWorkplaceConfig(workplaceId: string | number): Promise<WorkplaceConfig> {
  const response = await httpClient.get<WorkplaceConfig>(
    `workplaces/${workplaceId}/config/`
  )
  return response
}

/**
 * 获取职场完整配置（含餐段时间规则）- 企业端/商户端排期专用
 * @param workplaceId 职场ID
 * @returns 职场完整配置（含 meal_segments_detail.time_rule）
 * 
 * 时间规则字段说明：
 * - order_cutoff_time: 订餐截止时间 (HH:MM)
 * - cutoff_day_offset: 截止偏移天数（0=当天，-1=前一天）
 * - delivery_end_time: 预计送达时间 (HH:MM)
 * 
 * API 路径: GET /api/v1/workplaces/{id}/config/
 */
export async function getWorkplaceFullConfig(workplaceId: string | number): Promise<WorkplaceFullConfig> {
  const response = await httpClient.get<WorkplaceFullConfig | { data: WorkplaceFullConfig }>(
    `workplaces/${workplaceId}/config/`
  )
  // 兼容包装和非包装响应格式
  if (response && typeof response === 'object' && 'data' in response) {
    return (response as { data: WorkplaceFullConfig }).data
  }
  return response as WorkplaceFullConfig
}

